import * as React from 'react';
import cx from 'classnames';
import root from 'window-or-global';
import * as queryString from 'querystring';

import {ConditionalHoc, WithScroll} from '../../hocs';
import {errorTypes, video as videoConstant, svgs} from '../../constants';

import {VideoProps} from './types';

import Icon from '../icon/Icon';
import Link from '../link/Link';
import InactivityListener from '../inactivity-listener/InactivityListener';

import DashPlayer from './players/epic-player/dash-player/DashPlayer';
import {checkEpicHosted} from './players/epic-player/EpicPlayer';
import HlsPlayer from './players/epic-player/hls-player/HlsPlayer';
import HtmlPlayer from './players/html-player/HtmlPlayer';
import YoutubePlayer from './players/youtube-player/YoutubePlayer';

import ErrorBoundary from '../error-boundary/ErrorBoundary';
import ErrorView from '../error/ErrorView';

// @ts-ignore
import styles from './Video.pcss';

const shouldMonitorActivity = (props: VideoProps): boolean => !!props.loop && !!props.autoplay;

interface State {
    isPlaying?: boolean;
    hasCommands?: boolean;
}

class VideoComponent extends React.Component<VideoProps> {
    static defaultProps: Partial<VideoProps> & {hasCommands: boolean} = {
        autoplay: false,
        hasControls: true,
        hasFullScreen: true,
        loop: false
    };

    state: State = {
        hasCommands: false,
        ...this.getInitialPlayState()
    };

    videoRef: React.RefObject<HTMLDivElement> = React.createRef();

    getInitialPlayState(): State | undefined {
        const {autoplay, type} = this.props;
        const isTwitchVideo: boolean = type && type.includes('TWITCH');
        if (!isTwitchVideo && autoplay) {
            return {
                isPlaying: true,
                hasCommands: false
            };
        }
    }

    componentDidMount() {
        const {type} = this.props;
        const {EPICHOSTED, HTML, YOUTUBE} = videoConstant.types;
        const hasCommands = [EPICHOSTED, HTML, YOUTUBE].includes(type);
        this.setState({hasCommands});
    }

    componentWillUnmount() {
        this.pauseVideo();
    }

    shouldComponentUpdate(nextProps: VideoProps, nextState: State): boolean {
        return Boolean(this.props.url && this.props.url !== nextProps.url);
    }

    videoInView = (): boolean => {
        const viewportHeight: number = root && root.innerHeight;
        const videoElement = this.videoRef.current && this.videoRef.current.getBoundingClientRect();
        const hidden: boolean =
            !!videoElement && (videoElement.bottom < 0 || videoElement.top > viewportHeight);
        return !hidden;
    };

    /**
     * Retain a reference to the child video component
     *
     * @param {Element} ref - Video component element
     */
    setRef = ref => {
        const {type} = this.props;
        if (ref) this[type] = ref;
    };

    /**
     * Run a video command while optionally setting the playing state
     *
     * @param {VideoCommands} command -  The video command to run
     * @param {boolean} setPlayingState - Whether or not the video state should be set to playing
     */
    runVideoCommand = (command, setPlayingState) => {
        const {type} = this.props;
        const commandExists = this[type] && this[type][command];
        if (this.state.hasCommands && commandExists) {
            this[type][command]();
            this.setState({isPlaying: setPlayingState});
        }
    };

    /**
     * Toggle a video between the playing and paused states
     */
    toggleVideo = () => {
        const {type} = this.props;
        const {isPlaying} = this.state;
        const {PLAY, PAUSE, TOGGLE} = videoConstant.commands;
        let toggle = TOGGLE;

        if (type === videoConstant.types.YOUTUBE) {
            toggle = isPlaying ? PAUSE : PLAY;
        }

        this.runVideoCommand(toggle, !isPlaying);
    };

    pauseVideo = (): void => {
        console.log('pauseVideo', this.props.contentId);
        this.runVideoCommand(videoConstant.commands.PAUSE, false);
    };

    playVideo = (): void => {
        console.log('playVideo', this.props.contentId);
        this.runVideoCommand(videoConstant.commands.PLAY, true);
    };

    isMuted(): boolean {
        const {autoplay, muted} = this.props;
        return Boolean(muted || autoplay);
    }

    /**
     * Build out a Twitch stream URL
     *
     * @see {@link https://dev.twitch.tv/docs/embed#non-interactive-inline-frames-for-live-streams-and-vods}
     * @returns {string} A Twitch stream URL
     */
    buildTwitchSrc = () => {
        const {autoplay, contentId, type} = this.props;
        const isChannel = type === videoConstant.types.TWITCH_STREAM;

        const options = {
            [isChannel ? 'channel' : 'video']: contentId,
            autoplay,
            muted: this.isMuted()
        };

        return `${videoConstant.TWITCH_BASE_URL}/?${queryString.stringify(options)}`;
    };

    render() {
        const {
            accentColor,
            autoplay,
            className,
            contentId,
            epicStillType,
            epicStillDurationSeconds,
            hasControls,
            hasFullScreen,
            locale,
            loop,
            poster,
            rating,
            recipes,
            sources,
            title,
            url
        } = this.props;

        let inner,
            recipe,
            type = this.props.type;

        /**
         * In the case of epicHosted videos, we receive 'recipes' instead of sources.
         * The recipe for this client is selected via the available recipes and the
         * video playback capability of the client. Also it will attempt to get the locale
         * based video and fallback to english here if the locale video doesn't exist.
         */
        // console.debug(
        //     `Checking video for Epic hosted. Type: ${type}, Client rendered: ${!root.__server_side_render}`
        // );

        const shouldReturnRecipe =
            type === videoConstant.types.EPICHOSTED && !root.__server_side_render;

        if (shouldReturnRecipe) {
            const data = checkEpicHosted(recipes, locale);
            if (!data.found) return data.empty;
            recipe = data.recipe;
            type = data.type;
        }

        switch (type) {
            case videoConstant.types.DASH_WEBM:
            case videoConstant.types.DASH_FMP4:
                inner = (
                    <DashPlayer
                        autoplay={autoplay}
                        accentColor={accentColor}
                        className={styles.content}
                        epicStillType={epicStillType}
                        epicStillDurationSeconds={epicStillDurationSeconds}
                        hasControls={hasControls}
                        hasFullScreen={hasFullScreen}
                        locale={locale}
                        loop={loop}
                        muted={this.isMuted()}
                        poster={poster}
                        rating={rating}
                        recipe={recipe}
                        ref={this.setRef}
                        sources={sources}
                        title={title}
                        type={type}
                    />
                );
                break;
            case videoConstant.types.HLS:
                inner = (
                    <HlsPlayer
                        autoplay={autoplay}
                        accentColor={accentColor}
                        className={styles.content}
                        epicStillType={epicStillType}
                        epicStillDurationSeconds={epicStillDurationSeconds}
                        hasControls={hasControls}
                        hasFullScreen={hasFullScreen}
                        locale={locale}
                        loop={loop}
                        muted={this.isMuted()}
                        poster={poster}
                        rating={rating}
                        recipe={recipe}
                        ref={this.setRef}
                        sources={sources}
                        title={title}
                        type={type}
                    />
                );
                break;
            case videoConstant.types.YOUTUBE:
                inner = (
                    <YoutubePlayer
                        autoplay={autoplay}
                        className={styles.content}
                        contentId={contentId}
                        hasControls={hasControls}
                        hasFullScreen={hasFullScreen}
                        loop={loop}
                        muted={this.isMuted()}
                        ref={this.setRef}
                        title={title}
                    />
                );
                break;
            case videoConstant.types.HTML:
                inner = (
                    <HtmlPlayer
                        autoplay={autoplay}
                        accentColor={accentColor}
                        className={styles.content}
                        hasControls={hasControls}
                        hasFullScreen={hasFullScreen}
                        loop={loop}
                        muted={this.isMuted()}
                        poster={poster}
                        ref={this.setRef}
                        sources={sources}
                        title={title}
                        type={type}
                    />
                );
                break;
            case videoConstant.types.TWITCH:
            case videoConstant.types.TWITCH_STREAM:
                inner = (
                    <iframe
                        allowFullScreen={hasFullScreen}
                        className={styles.content}
                        id={contentId}
                        src={this.buildTwitchSrc()}
                        frameBorder="0"
                        referrerPolicy="no-referrer"
                        title={title}
                    />
                );
                break;
            case videoConstant.types.EXTERNAL:
                inner = (
                    <Link className={styles.externalLink} to={url}>
                        {poster && (
                            <img
                                alt={title}
                                className={cx(styles.content, styles.poster)}
                                src={poster}
                            />
                        )}
                        <Icon className={styles.playExternal} icon={svgs.PLAY_EXTERNAL} />
                    </Link>
                );
                break;
            default:
                return null;
        }

        const videoElement: React.ReactNode = (
            <div className={cx(styles.wrapper, styles[type], className)} ref={this.videoRef}>
                {inner}
            </div>
        );

        if (shouldMonitorActivity(this.props)) {
            return (
                <InactivityListener
                    onIdle={this.pauseVideo}
                    onReturnFromIdle={this.playVideo}
                    shouldListen={this.videoInView()}>
                    {videoElement}
                </InactivityListener>
            );
        }

        return videoElement;
    }
}

// Wrap video in Error Boundary
const Video = props => (
    <ErrorBoundary errorView={<ErrorView showButtons={false} type={errorTypes.CLIENT_ERROR} />}>
        <VideoComponent {...props} />
    </ErrorBoundary>
);

export default ConditionalHoc(WithScroll, Video, shouldMonitorActivity);
